/**
 * Presentación de importes y cantidades — `URS §129`, `TDS-CORE` Parte 2 §4.
 *
 * La API manda los importes ya calculados, como TEXTO con sus decimales
 * exactos ("3448,50", "1,000"). Aquí sólo se cambia su ASPECTO:
 *
 * ```text
 * "3448,50"  -> "3.448,50"   separador de miles
 * "1,000"    -> "1"          cantidad sin ceros que no dicen nada
 * "250,0000" -> "250"
 * ```
 *
 * **Nada de esto convierte el texto a número.** Ni `Number`, ni `parseFloat`,
 * ni aritmética: un `0,1 + 0,2` de JavaScript reintroduciría el error que el
 * cálculo con enteros de `fiscal-core` evita, y la pantalla acabaría mostrando
 * un total distinto del guardado. `tests/adversarial/web-shell.mjs` lo vigila.
 */

/** Agrupa de tres en tres la parte entera: "3448,50" -> "3.448,50". */
export function conMiles(importe: string): string {
  const negativo = importe.startsWith("-");
  const sinSigno = negativo ? importe.slice(1) : importe;
  const [entera, decimales] = sinSigno.split(",");
  if (entera === undefined || !/^\d+$/.test(entera)) return importe;
  let agrupada = "";
  for (let i = 0; i < entera.length; i++) {
    if (i > 0 && (entera.length - i) % 3 === 0) agrupada += ".";
    agrupada += entera[i];
  }
  const cuerpo = decimales === undefined ? agrupada : `${agrupada},${decimales}`;
  return negativo ? `-${cuerpo}` : cuerpo;
}

/** Quita los ceros finales de los decimales: "1,000" -> "1"; "2,500" -> "2,5". */
export function sinCerosSobrantes(cantidad: string): string {
  if (!cantidad.includes(",")) return cantidad;
  const recortada = cantidad.replace(/0+$/, "");
  return recortada.endsWith(",") ? recortada.slice(0, -1) : recortada;
}

/**
 * Un precio unitario, que se guarda con hasta 4 decimales: con miles, y con
 * los céntimos siempre a la vista pero sin los ceros que sobran.
 *
 * ```text
 * "12,0000" -> "12,00"     (antes salía "12,0000 €" en Productos)
 * "100,5"   -> "100,50"    (antes salía "100,5 €" en el presupuesto)
 * "0,1250"  -> "0,125"     el tercer y cuarto decimal se ven si los hay
 * ```
 */
export function precioLegible(precio: string): string {
  if (!/^-?\d+(,\d*)?$/.test(precio)) return precio;
  const [entera, decimales = ""] = precio.split(",");
  const recortados = decimales.replace(/0+$/, "").padEnd(2, "0");
  return conMiles(`${entera},${recortados}`);
}

/** Lo que se lee en un documento: miles y sin decimales vacíos. */
export function cantidadLegible(cantidad: string): string {
  return conMiles(sinCerosSobrantes(cantidad));
}

/** IBAN en grupos de cuatro para leerlo y copiarlo: "ES91 2100 0418 …". */
export function ibanLegible(iban: string): string {
  return iban.replace(/(.{4})(?=.)/g, "$1 ");
}

/** Importe con miles y sus dos decimales tal cual llegan. */
export function importeLegible(importe: string): string {
  return conMiles(importe);
}

/**
 * "2026-11-30" -> "30 de noviembre de 2026". Es un día natural: sin zona
 * horaria. Todas las fechas de la aplicación se leen así: antes unas pantallas
 * enseñaban "2026-11-06" tal cual llega de la API y otras el día con su mes.
 */
export function diaLegible(dia: string): string {
  const [y, m, d] = dia.split("-").map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "2026-11-30" -> "30/11/2026": como lo escribe el PDF del documento. */
export function diaDeDocumento(dia: string): string {
  const [a, m, d] = dia.split("-");
  return a === undefined || m === undefined || d === undefined ? dia : `${d}/${m}/${a}`;
}

/**
 * Lo que se escribe en un campo de importe, listo para la API: "1.500,50" ->
 * "1500,50". Solo se quitan los puntos que son de MILES (grupos de tres
 * cifras); cualquier otra cosa se manda tal cual y la API dice qué falla.
 *
 * Lo pidió el propietario tras probar en staging (2026-10-25): Oportunidades
 * aceptaba "1.500,50", y Cobros y Compras lo rechazaban pidiendo escribirlo
 * "sin puntos de miles". Tampoco aquí se convierte nada a número.
 */
export function sinPuntosDeMiles(escrito: string): string {
  const v = escrito.trim();
  return /^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(v) ? v.replace(/\./g, "") : v;
}
